import { Fragment, useState } from 'react';

import { DisconnectOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import { Card, Col, Popover, Row, Space } from 'antd';
import { AchBankLinkConstant } from 'src/constants';
import { useApplicationConfigs } from 'src/hooks';
import { MModalV2, MButton, MDivider, MTypography } from 'src/lib';
import { Color, Image, Spacing } from 'src/styles';

import { YourAchBankLinkNotApprovedAlertMessage } from '../YourAchBankLinkNotApprovedAlertMessage/YourAchBankLinkNotApprovedAlertMessage';
import { YourTransferNotCompletedAlertMessage } from '../YourTransferNotCompletedAlertMessage/YourTransferNotCompletedAlertmessage';

import { AchBankLinkStatusTag } from './AchBankLinkStatusTag';
import * as Styles from './BankAccountItem.styles';

export enum ACHRelationshipActionType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  EDIT = 'edit',
  UNLINK = 'unlink',
}

export const achRelationshipsActions = [
  {
    label: 'Edit Nickname',
    Icon: EditOutlined,
    type: ACHRelationshipActionType.EDIT,
  },
  {
    label: 'Unlink',
    Icon: DisconnectOutlined,
    type: ACHRelationshipActionType.UNLINK,
  },
];

const BankAccountItem = ({ account, onClick, onDeposit, onWithdrawal, onEdit, onUnlink }: any) => {
  const [visible, setIsVisible] = useState(false);
  const [isDisabledDepositModalOpen, setIsDisabledDepositModalOpen] = useState(false);
  const [isDisabledWithdrawalModalOpen, setIsDisabledWithdrawalModalOpen] = useState(false);

  const { deposit, withdrawal } = useApplicationConfigs();

  const isBankLinkPending = () =>
    AchBankLinkConstant.PENDING_STATUS_LIST.includes(account?.tradingBlockACHRelationship.state);

  const handleVisibleChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  const onCloseDisabledTransferModal = () => {
    if (isDisabledDepositModalOpen) {
      setIsDisabledDepositModalOpen(false);

      return;
    }
    setIsDisabledWithdrawalModalOpen(false);
  };

  const onDropDownClick = (action: { type: ACHRelationshipActionType }) => {
    if (action.type === ACHRelationshipActionType.EDIT) {
      setIsVisible(false);
      onEdit();
    } else if (action.type === ACHRelationshipActionType.UNLINK) {
      setIsVisible(false);
      onUnlink();
    }
  };

  const onDepositAction = () => {
    if (deposit.isDisabled) {
      setIsDisabledDepositModalOpen(true);

      return;
    }
    onClick();

    return onDeposit();
  };

  const onWithdrawalAction = () => {
    if (withdrawal.isDisabled) {
      setIsDisabledWithdrawalModalOpen(true);

      return;
    }
    onClick();
    onWithdrawal();
  };

  return (
    <Fragment>
      <Card className={Styles.cardWrapper}>
        <Row justify={'center'}>
          <Col>
            <Space direction='horizontal' align='start'>
              <div className={Styles.bankImageContainer}>
                <img
                  alt=''
                  src={
                    account?.tradingBlockPlaidInstitution?.logo
                      ? `data:image/jpeg;base64,${account?.tradingBlockPlaidInstitution?.logo}`
                      : Image.BankPlaceholder
                  }
                  className={Styles.bankImage}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  justifyContent: 'center',
                }}>
                <>
                  <Row>
                    <MTypography variant='body1' color={Color.Gray8}>
                      {account.tradingBlockACHRelationship?.bankAccountType}
                    </MTypography>
                    {account?.tradingBlockACHRelationship?.nickName && (
                      <MTypography
                        variant='body1'
                        color={Color.Gray6}
                        className={
                          Styles.nicknameLabel
                        }>{`(${account.tradingBlockACHRelationship.nickName})`}</MTypography>
                    )}
                  </Row>
                  <MTypography variant='body1' weight='bold' color={Color.Gray7} className={Styles.bankAccountLabel}>
                    {account?.tradingBlockACHRelationship?.bankAccount?.replace(/^(.{3})(.{3})(.*)$/, '$1 $2 $3')}
                  </MTypography>
                </>
              </div>
              <AchBankLinkStatusTag
                state={account?.tradingBlockACHRelationship.state}
                testId={'link-bank-account-status'}
              />
            </Space>
          </Col>
          <Col flex='auto'>
            <div className={Styles.endTextAlignment} onClick={onClick}>
              <Popover
                getPopupContainer={(trigger: any) => trigger.parentElement}
                content={
                  <div className={Styles.popoverContent}>
                    <MTypography variant='h6' weight='bold' color={Color.Gray9} className={Styles.popoverTitle}>
                      What would you like to do
                    </MTypography>
                    <div>
                      {achRelationshipsActions.map((action, index) => {
                        const ActionIcon = action.Icon;
                        const colorFont =
                          action.type === ACHRelationshipActionType.EDIT
                            ? Color.Gray8
                            : action.type === ACHRelationshipActionType.UNLINK
                            ? Color.Red6
                            : Color.Brand6;

                        return (
                          <div
                            key={`${action.label}_key`}
                            className={Styles.actionRowContainer}
                            style={{
                              borderBottom: index === achRelationshipsActions.length - 1 ? '0px' : '1px solid #F2F5F7',
                            }}>
                            <div
                              className={Styles.actionRow}
                              style={{
                                color: colorFont,
                              }}
                              onClick={() => onDropDownClick(action)}
                              data-testid={`link-bank-account-btn-${action.label.toLowerCase()}`}>
                              <ActionIcon className={Styles.actionImage} style={{ color: colorFont }} />
                              {action.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
                trigger='click'
                open={visible}
                onOpenChange={handleVisibleChange}
                placement='bottomRight'>
                <MoreOutlined className={Styles.moreVerticalImage} />
              </Popover>
            </div>
          </Col>
          <MDivider />
          <Col span={24} className={Styles.endTextAlignment}>
            <YourTransferNotCompletedAlertMessage isAchBankLinkPending={isBankLinkPending()} />
            <YourAchBankLinkNotApprovedAlertMessage isAchBankLinkPending={isBankLinkPending()} />
            <Space direction='horizontal'>
              <MButton
                onClick={onDepositAction}
                type='secondary'
                className={Styles.transferButton}
                testId={'money-transfers-btn-deposit'}>
                Deposit Funds
              </MButton>
              <MButton
                onClick={onWithdrawalAction}
                type='secondary'
                className={Styles.transferButton}
                testId={'money-transfers-btn-withdrawal'}>
                Withdrawal Funds
              </MButton>
            </Space>
          </Col>
        </Row>
      </Card>

      <MModalV2
        title='Bank Account Transfering Information'
        open={isDisabledDepositModalOpen || isDisabledWithdrawalModalOpen}
        onClose={onCloseDisabledTransferModal}
        body={isDisabledDepositModalOpen ? deposit.disclaimer : withdrawal.disclaimer}
        footer={
          <MButton type='tertiary' onClick={onCloseDisabledTransferModal} className={Spacing.my4}>
            Close
          </MButton>
        }
      />
    </Fragment>
  );
};

export default BankAccountItem;
